import type { TravelArticle, TravelImage } from "../../types";

// Photos live in src/assets/travel/singapore/ — cropped to the frames the article uses
// (16:9 landscape, 3:4 portrait) and stripped of EXIF/GPS. Parcel resizes them and
// converts them to WebP at build time (the ?as=webp&width=… query); the paths must stay
// string literals inside new URL() so Parcel can find them.
const responsive = (
  sm: string,
  lg: string,
  widths: [number, number],
): TravelImage => ({
  src: sm,
  srcSet: `${sm} ${widths[0]}w, ${lg} ${widths[1]}w`,
});

const skyline = responsive(
  new URL(
    "../../assets/travel/singapore/skyline.jpg?as=webp&width=900&quality=78",
    import.meta.url,
  ).href,
  new URL(
    "../../assets/travel/singapore/skyline.jpg?as=webp&width=1800&quality=78",
    import.meta.url,
  ).href,
  [900, 1800],
);

const supertrees = responsive(
  new URL(
    "../../assets/travel/singapore/supertrees.jpg?as=webp&width=900&quality=78",
    import.meta.url,
  ).href,
  new URL(
    "../../assets/travel/singapore/supertrees.jpg?as=webp&width=1800&quality=78",
    import.meta.url,
  ).href,
  [900, 1800],
);

const hawkerChan = responsive(
  new URL(
    "../../assets/travel/singapore/hawker-chan.jpg?as=webp&width=900&quality=78",
    import.meta.url,
  ).href,
  new URL(
    "../../assets/travel/singapore/hawker-chan.jpg?as=webp&width=1800&quality=78",
    import.meta.url,
  ).href,
  [900, 1800],
);

const sultanMosque = responsive(
  new URL(
    "../../assets/travel/singapore/sultan-mosque.jpg?as=webp&width=900&quality=78",
    import.meta.url,
  ).href,
  new URL(
    "../../assets/travel/singapore/sultan-mosque.jpg?as=webp&width=1800&quality=78",
    import.meta.url,
  ).href,
  [900, 1800],
);

const universalStudios = responsive(
  new URL(
    "../../assets/travel/singapore/universal-studios.jpg?as=webp&width=700&quality=78",
    import.meta.url,
  ).href,
  new URL(
    "../../assets/travel/singapore/universal-studios.jpg?as=webp&width=1400&quality=78",
    import.meta.url,
  ).href,
  [700, 1400],
);

const merlion = responsive(
  new URL(
    "../../assets/travel/singapore/merlion.jpg?as=webp&width=700&quality=78",
    import.meta.url,
  ).href,
  new URL(
    "../../assets/travel/singapore/merlion.jpg?as=webp&width=1400&quality=78",
    import.meta.url,
  ).href,
  [700, 1400],
);

const singapore: TravelArticle = {
  slug: "singapore",
  place: "Singapore",
  country: "Singapore",
  coord: "1.3°N · 103.8°E",
  date: "July 2022",
  hero: skyline,
  title: "Singapore - My first solo",
  body: [
    {
      t: "p",
      drop: true,
      text: "Those who have known me since my 20s or earlier can vouch for the fact that I was never someone who could go on a solo trip. I have always been a bit vanilla when it comes to getting out of my comfort zone. But the year 2022 was a game changer.",
    },
    {
      t: "p",
      text: "The world was recovering from the aftermath of covid and many people had started travelling again. I also wanted to go somewhere outside India. However, none of my friends were willing to travel. Some had earlier commitments while others were simply recuperating from the anxiety that the pandemic brought. So, I decided to venture out alone. My therapist was very pleased with my decision to push past my comfort zone and I was looking at this as more of a character development task than a pleasure activity. Singapore was an instant choice. It was safe, and a big city to start with seemed like a smart decision.",
    },
    {
      t: "p",
      text: "After arriving in Singapore and successfully reaching my hotel room, I looked at myself in the mirror and thought, “This is your first hotel room all to yourself. Many more to come 🤞🏻”. The room was neat and very space-efficient. I was quite content to have made it this far. It seemed that I had made it in life for the first time ever. The initial guilt of spending money on a vacation, all for myself, was gone. For a second there, I was proud of myself.",
    },
    {
      t: "p",
      text: "In the evening, I went to Gardens by the Bay to watch Garden Rhapsody which is a light and sound show at the Supertree Grove. That day the theme was “Enchanted Woods”. I had gone there with no expectations and this experience turned out to be so mesmerising that I had tears of joy. I knew I was making my inner child happy. This was one of the many new feelings I experienced as a solo traveller.",
    },
    {
      t: "photo",
      ...supertrees,
      alt: "Two Supertrees lit in pink and white against a black night sky",
      caption: "Garden Rhapsody at the Supertree Grove — the night the theme was “Enchanted Woods”.",
    },
    {
      t: "p",
      text: "As a foodie, I found Singapore to be a dream destination. It is famous for its Michelin star hawker food stalls. I had done a little bit of research and knew a few places I wanted to cover for my food tour. Among them was the famous soy chicken and rice by Hawker Chan in Chinatown. I knew what to order but I didn’t know how to use chopsticks. I had theoretical knowledge and zero practical confidence. The choices were clear — leave and don’t try the food or adapt. To my surprise, I did adapt. I finished the whole meal (which was delicious) using chopsticks. I remember this as one of my first lessons in confidence. That day, at that exact restaurant, I knew that I could make it through this whole vacation without failing. (“Failing” at what exactly?! I was too hard on myself.)",
    },
    {
      t: "photo",
      ...hawkerChan,
      alt: "A plate of soy sauce chicken, rice, cucumber slices and a braised egg on a Hawker Chan tray",
      caption: "The soy chicken and rice at Hawker Chan, Chinatown — finished, chopsticks and all.",
    },
    {
      t: "p",
      text: "As I explored this city-state, I realised that Singapore is one of the best examples of an amalgamation of cultures, where each one holds its own and they merge seamlessly and peacefully into one nation. The beautiful Sultan Mosque, the bustling Little India, the colourful Chinatown — all places stood their ground, rooted in their culture but also blended into this vibrant city. Another fascinating thing was the public transport. Hats off to this marvel of a system. I have travelled to many places since this trip but by far, Singapore happens to be the number one country for me when it comes to public transport and traffic management.",
    },
    {
      t: "photo",
      ...sultanMosque,
      alt: "The golden dome and lit minaret of the Sultan Mosque at night, with office towers behind",
      caption: "The Sultan Mosque after dark, holding its ground against the skyline.",
    },
    {
      t: "p",
      text: "The highlight of this entire trip was a day on Sentosa Island. Universal Studios was a dream come true for the little girl who loved Jurassic Park and Shrek. That entire day I did not feel like I needed anyone’s company. Not for a single moment. I went for the rides and tried a strawberry slushie. I took photos with Shrek and Fiona, one of the Minions (I’ve forgotten which one) and Tigress. It was the best day ever!",
    },
    {
      t: "photo",
      portrait: true,
      ...universalStudios,
      alt: "Megholova standing between Shrek and Fiona in front of a fountain at Universal Studios",
      caption: "With Shrek and Fiona at Universal Studios, Sentosa Island. The best day ever.",
    },
    {
      t: "p",
      text: "However, not everything went according to plan on this trip. I was supposed to take a Hop-On Hop-Off tour bus (highly recommended by the way!) on a Wednesday but had no idea that operations were closed that day. I walked to the counter and found that everything was shut down. My travel agency had no idea! Normally I would have been annoyed but then I decided that I couldn’t waste my day being angry with other people. So I walked and explored a lot of the city on foot. That too is an experience in itself! I learned to find solutions in situations where things don’t go as planned. That was the day I took the Merlion photo which my travel agency was kind enough to post on their page.",
    },
    {
      t: "photo",
      portrait: true,
      ...merlion,
      alt: "Megholova in front of the Merlion fountain, with the Singapore skyline behind",
      caption: "The Merlion — taken on the day the tour bus wasn’t running, and I walked instead.",
    },
    { t: "divider" },
    {
      t: "p",
      text: "2022 was a big year for me. I pushed myself out of my comfort zone by going on a solo trip. I wouldn’t say it didn’t feel lonely at times. Especially when you are in a new country where no one knows you, you tend to feel like your world might collapse. Nevertheless, nothing in life humbles you more than a solo trip. When plans don’t work out and you improvise, when you feel lonely but you learn to overcome it, when you realise that you can enjoy life all by yourself — all of this happens on that first solo trip you dare to take. And there is no looking back after that.",
    },
  ],
};

export default singapore;
